import { z } from 'zod'

export const propertySchema = z
  .object({
    name: z.string().min(1, 'Property name is required'),
    address: z.string().optional(),
    // EP-111 T111-7 — re-introduced property_type (standalone-form-only, gated
    // on settings.condominium_enabled). Optional: hidden entirely when the
    // toggle is off, so the field is never registered/validated in that case.
    property_type: z.enum(['apartment', 'condominium']).optional(),
    // WEB-M12: these stay z.number() because BOTH register sites use
    // { valueAsNumber: true } (PropertyFormFields) — values are numbers before
    // validation. Do not register a number field without valueAsNumber.
    billing_day: z.number().int().min(1).max(28),
    payment_due_day: z.number().int().min(1).max(31).nullable().optional(),
    deposit_type: z.enum(['months', 'fixed']),
    deposit_value: z.number().min(0, 'Deposit value must be positive'),
    tax_id: z.string().optional(),
    branch_number: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().email('Invalid email address').optional().or(z.literal('')),
    location_url: z.string().url('Invalid URL').optional().or(z.literal('')),
    // EP-136 (T136-026) — booking settings, per-key override of the account-
    // level fallback (see web/src/lib/bookingSettings.ts). Each `_override`
    // boolean is "does THIS property set this key" — the value field always
    // carries a concrete value (pre-filled from the resolved account
    // default) so it's sent to the API only when its override flag is on.
    booking_fee_mode_override: z.boolean().optional(),
    booking_fee_mode: z.enum(['deposit', 'revenue']).optional(),
    booking_fee_default_override: z.boolean().optional(),
    booking_fee_default: z.number().optional(),
    booking_fee_forfeit_on_cancel_override: z.boolean().optional(),
    booking_fee_forfeit_on_cancel: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // Mirrors deposit_value's min(0) requirement, but only while the
    // property actually overrides booking_fee_default — an un-overridden
    // field is never submitted (buildBookingSettingsPayload drops it), so it
    // has nothing to validate.
    if (
      data.booking_fee_default_override &&
      (data.booking_fee_default === undefined ||
        Number.isNaN(data.booking_fee_default) ||
        data.booking_fee_default < 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['booking_fee_default'],
        message: 'Enter a booking amount, or turn this override off',
      })
    }
  })

export type PropertyFormData = z.infer<typeof propertySchema>
